import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { find, propEq, includes, filter } from 'ramda';

import s from './MapNavigator.module.scss';

const levelShape = PropTypes.shape({
    id: PropTypes.string,
    ordinal: PropTypes.number,
    details: PropTypes.string,
    name: PropTypes.string,
});

const structureShape = PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    levels: PropTypes.arrayOf(levelShape),
});

export default class MapNavigator extends Component {
    static propTypes = {
        structures: PropTypes.arrayOf(structureShape),
        levels: PropTypes.arrayOf(levelShape),
        selectedStructure: structureShape,
        selectedLevel: levelShape,
        selectLevel: PropTypes.func,
        selectStructure: PropTypes.func,
        levelsWithMapNotes: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.string)),
    };

    static defaultProps = {
        structures: [],
        levels: [],
        selectStructure: () => {},
        selectLevel: () => {},
        selectedStructure: undefined,
        selectedLevel: undefined,
        levelsWithMapNotes: [],
    };

    constructor(props) {
        super(props);
        this.state = { expanded: false };
    }

    toggleExpanded = () => this.setState(state => ({ expanded: !state.expanded }));

    // render a (possibly-selected) structure
    renderStructure = structure => {
        const { levelsWithMapNotes, levels } = this.props;
        const selectedStructure = this.props.selectedStructure;
        const isExpanded = selectedStructure && structure.id === selectedStructure.id;
        const hasOpenMapNotesInStructure = !!find(propEq(structure.id, 'structureId'))(levelsWithMapNotes);

        /**
         * Adds a section under each listed structure for each level in that structure.
         *
         * @param {object} level
         */
        const renderLevel = level => {
            const hasOpenMapNotesInLevel = hasOpenMapNotesInStructure && includes({ ordinalId: level.ordinalId, structureId: structure.id }, levelsWithMapNotes);
            return (
                <div key={level.id} className={classNames(s.level, { [s.selected]: level === this.props.selectedLevel })} onClick={() => this.props.selectLevel(level)}>
                    <div className={s.labelWrapper}>
                        {level.name}&nbsp;&nbsp;
                        {'\u2013'}&nbsp;&nbsp;
                        {level.details}
                        {hasOpenMapNotesInLevel ? this.renderIcon() : <div />}
                    </div>
                </div>
            );
        };

        const levelsForStructure = filter(l => l.structureId === structure.id, levels);

        return (
            <div className={s.structureWrapper} key={structure.id}>
                <div className={classNames(s.structure, { [s.selected]: isExpanded })} onClick={() => this.props.selectStructure(structure)}>
                    <div className={s.labelWrapper}>
                        {structure.name}
                        {hasOpenMapNotesInStructure ? this.renderIcon() : <div />}
                    </div>
                </div>
                <div className={classNames(s.levelsWrapper, { [s.expanded]: isExpanded })}>{levelsForStructure.map(renderLevel)}</div>
            </div>
        );
    };

    // render the bottom button of the structure selector that shows the currently-selected structure if there is one
    renderSelectedStructure = () => {
        const { expanded } = this.state;
        const { selectedStructure, selectedLevel } = this.props;

        if (!selectedStructure || !selectedLevel) return;

        const subtitle = selectedLevel ? `${selectedStructure.name} - ${selectedLevel.name}` : null;
        const title = selectedLevel ? selectedLevel.details : 'Select a building';

        return (
            <div className={classNames(s.selectedStructure, { [s.expanded]: expanded })} onClick={this.toggleExpanded}>
                <div className={s.selectedStructureTitleWrapper}>
                    {subtitle && <div className={classNames(s.subtitle, s.labelWrapper)}>{subtitle}</div>}
                    <div className={classNames(s.title, s.labelWrapper)}>{title}</div>
                </div>
                <div className={classNames(expanded ? 'icon-triangleup' : 'icon-triangledown', s.icon)} />
            </div>
        );
    };

    renderIcon = () => <span className={classNames('icon-newmapnote', s.mapNoteIndicator)} />;

    render() {
        return (
            <div className={classNames(s.mapNavigator)}>
                {/* pop-up structure list */}
                <div className={s.structureListWrapper}>
                    <div className={classNames(s.structureList, { [s.expanded]: this.state.expanded })}>{this.props.structures.map(this.renderStructure)}</div>
                </div>

                {/* bottom-button from which list pops up */}
                {this.renderSelectedStructure()}
            </div>
        );
    }
}
